import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDateTime, fmtNumber } from '../lib/format';
import { Button, Field, Input, useToast } from './ui';

export type ReaderStatus = {
  connected: boolean;
  keyHint: string | null;
  model: string;
  models: Record<string, string>;
  monthlyLimit: number;
  connectedAt: string | null;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastError: string | null;
  usage: { reads: number; inputTokens: number; outputTokens: number };
};

export const useReaderStatus = () => useQuery({ queryKey: ['reader'], queryFn: () => api<ReaderStatus>('/api/integrations/reader') });

/** Settings → the reading agent's AI key (Anthropic), model and monthly cap. */
export function ReaderSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const status = useReaderStatus();
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState<string | null>(null);
  const [limit, setLimit] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const s = status.data;
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ['reader'] });
      setApiKey('');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const chosenModel = model ?? s?.model ?? 'claude-sonnet-5-5';
  const chosenLimit = Number(limit ?? s?.monthlyLimit ?? 300) || 300;

  return (
    <div className="panel panel-body">
      <div className="form-section">
        <div className="form-section-title">סוכן קריאה — רשימות מוצרים מתמונה / PDF / טקסט</div>
        <p className="small muted" style={{ marginTop: 0 }}>
          הסוכן קורא רשימות שלקוחות שולחים (תמונה, צילום מסך, PDF או טקסט), מתאים כל שורה למוצר בקטלוג ופותח טיוטת הצעת מחיר במחירים של הלקוח.
          לקריאת תמונות הוא משתמש ב-Claude של Anthropic — מפתח API נפתח ב-<bdi className="ltr">console.anthropic.com</bdi> (Settings ← API Keys), אחרי טעינת קרדיט.
          המפתח נשמר מוצפן ולא מוצג שוב. טקסט מודבק עובד גם בלי מפתח (התאמה בסיסית).
        </p>
        {s?.connected ? (
          <div className="stack">
            <dl className="kv">
              <dt>סטטוס</dt>
              <dd>{s.lastTestOk === false ? <span className="badge bad">החיבור נכשל</span> : <span className="badge ok">מחובר</span>}</dd>
              <dt>מפתח</dt>
              <dd><bdi className="ltr">{s.keyHint}</bdi></dd>
              <dt>החודש</dt>
              <dd>
                {fmtNumber(s.usage.reads)} קריאות מתוך {fmtNumber(s.monthlyLimit)} · {fmtNumber(Math.round((s.usage.inputTokens + s.usage.outputTokens) / 1000))} אלף טוקנים
              </dd>
              <dt>בדיקה אחרונה</dt>
              <dd>{s.lastTestAt ? fmtDateTime(s.lastTestAt) : '—'}{s.lastError ? <span className="small" style={{ color: 'var(--bad)' }}> · {s.lastError}</span> : null}</dd>
            </dl>
            <div className="form-grid">
              <Field label="מודל">
                <select className="select" value={chosenModel} onChange={(e) => setModel(e.target.value)} disabled={!isAdmin}>
                  {Object.entries(s.models).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </Field>
              <Field label="מכסת קריאות לחודש" hint="מעבר למכסה הסוכן נעצר עד החודש הבא — הגנה מפני חיוב לא צפוי">
                <Input type="number" min={1} max={10000} value={limit ?? String(s.monthlyLimit)} onChange={(e) => setLimit(e.target.value)} ltr disabled={!isAdmin} />
              </Field>
            </div>
            <div className="row wrap">
              {model != null || limit != null ? (
                <Button variant="primary" loading={busy === 'save'} disabled={!isAdmin} onClick={() => run('save', () => api('/api/integrations/reader/settings', { method: 'PUT', body: { model: chosenModel, monthlyLimit: chosenLimit } }).then(() => (setModel(null), setLimit(null))))}>
                  שמירה
                </Button>
              ) : null}
              <Button onClick={() => run('test', () => api('/api/integrations/reader/test', { body: {} }))} loading={busy === 'test'} disabled={!isAdmin}>בדיקת חיבור</Button>
              <Button variant="ghost" onClick={() => run('off', () => api('/api/integrations/reader', { method: 'DELETE' }))} loading={busy === 'off'} disabled={!isAdmin}>ניתוק</Button>
            </div>
          </div>
        ) : (
          <div className="stack">
            <div className="form-grid">
              <Field label="מפתח API של Anthropic" required hint="מתחיל ב-sk-ant-">
                <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} ltr autoComplete="new-password" disabled={!isAdmin} />
              </Field>
              <Field label="מודל">
                <select className="select" value={chosenModel} onChange={(e) => setModel(e.target.value)} disabled={!isAdmin}>
                  {Object.entries(s?.models ?? { 'claude-sonnet-5-5': 'Claude Sonnet 5.5' }).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </Field>
              <Field label="מכסת קריאות לחודש">
                <Input type="number" min={1} max={10000} value={limit ?? '300'} onChange={(e) => setLimit(e.target.value)} ltr disabled={!isAdmin} />
              </Field>
            </div>
            <div className="row">
              <Button variant="primary" loading={busy === 'connect'} disabled={!isAdmin || apiKey.trim().length < 20} onClick={() => run('connect', () => api('/api/integrations/reader', { method: 'PUT', body: { apiKey: apiKey.trim(), model: chosenModel, monthlyLimit: chosenLimit } }))}>
                חיבור ובדיקה
              </Button>
              {!isAdmin ? <span className="small muted">רק מנהל יכול לחבר את הסוכן.</span> : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
